// ConversationPicker — the chat's conversation head, pure (pen "Chat header"
// and "Conversation picker": a pill with the shown conversation's title that
// opens the list, then "New").
//
// The title truncates to one line; the list scrolls, newest first; with more
// than `SEARCH_FROM` rows a field finds one by title (pen: "search when there
// are more than 8"); an empty list says so; a failed read offers a retry.
// Picking a row reports it up and closes the list. With `actions` (a shown
// conversation that has started), "…" offers Rename (the title becomes a
// field in place: Enter or leaving it saves, Escape cancels) and Delete
// (the host asks first).
//
// The list's open state, the search text and the rename field are view-
// ephemeral (they survive no reload, carry no domain meaning), so they are
// local state, like every popover's.

export { ConversationPicker };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Input } from "../Input/Input.js";
import { Menu } from "../Menu/Menu.js";
import { Popover } from "../Popover/Popover.js";
import "./ChatPanel.css";

// The list grows a search field past this many rows.
const SEARCH_FROM = 8;

// The rename field takes the focus, its text selected, once the menu that
// asked for it has closed: a DOM concern, no state. Module-level, so React
// calls it on mount only, never on each keystroke's render.
const focusOnMount = (el: HTMLInputElement | null): void => {
    if (el !== null)
        requestAnimationFrame(() => {
            el.focus();
            el.select();
        });
};

type ConversationPickerRow = {
    readonly id: string;
    readonly title: string;
    readonly when?: string;
    readonly active: boolean;
};

type ConversationPickerProps = {
    // The shown conversation's title ("New conversation" before it has one).
    readonly title: string;
    readonly status: "pending" | "failed" | "ready";
    readonly rows: readonly ConversationPickerRow[];
    readonly onOpen: (id: string) => void;
    readonly onNew: () => void;
    readonly onRetry: () => void;
    // Rename and Delete the shown conversation. Absent: nothing to act on.
    readonly actions?: {
        // The title as it stands, to edit (empty: none chosen yet).
        readonly current: string;
        readonly maxLength: number;
        readonly onRename: (title: string) => void;
        readonly onDelete: () => void;
    };
};

const ConversationPicker = ({
    title,
    status,
    rows,
    onOpen,
    onNew,
    onRetry,
    actions,
}: ConversationPickerProps): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [renaming, setRenaming] = useState<string | null>(null);
    const needle = query.trim().toLocaleLowerCase();
    const shown =
        needle === ""
            ? rows
            : rows.filter((r) => r.title.toLocaleLowerCase().includes(needle));
    const list: ReactNode =
        status === "pending" ? null : status === "failed" ? (
            <div className="bilda-chat-panel__note" role="status">
                <span>{t`Couldn't load conversations`}</span>
                <button
                    type="button"
                    className="bilda-chat-panel__retry"
                    onClick={onRetry}
                >
                    {t`Try again`}
                </button>
            </div>
        ) : rows.length === 0 ? (
            <p className="bilda-chat-panel__note">{t`No conversations yet`}</p>
        ) : shown.length === 0 ? (
            <p className="bilda-chat-panel__note">{t`No conversation matches`}</p>
        ) : (
            <div className="bilda-chat-panel__picker-list">
                {shown.map((row) => (
                    <button
                        key={row.id}
                        type="button"
                        className="bilda-chat-panel__picker-row"
                        aria-current={row.active ? "true" : undefined}
                        data-active={row.active ? "true" : undefined}
                        title={row.title}
                        onClick={() => {
                            setOpen(false);
                            setQuery("");
                            onOpen(row.id);
                        }}
                    >
                        <span className="bilda-chat-panel__picker-title">
                            {row.title}
                        </span>
                        {row.when !== undefined ? (
                            <span className="bilda-chat-panel__picker-when">
                                {row.when}
                            </span>
                        ) : null}
                    </button>
                ))}
            </div>
        );
    const saveRename = (text: string) => {
        setRenaming(null);
        const next = text.trim();
        if (actions !== undefined && next !== "" && next !== actions.current)
            actions.onRename(next);
    };
    const head: ReactNode =
        renaming !== null && actions !== undefined ? (
            <Input
                className="bilda-chat-panel__rename"
                aria-label={t`Conversation name`}
                value={renaming}
                maxLength={actions.maxLength}
                inputRef={focusOnMount}
                onChange={(e) => setRenaming(e.target.value)}
                onBlur={(e) => saveRename(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename(renaming);
                    if (e.key === "Escape") setRenaming(null);
                }}
            />
        ) : (
            <Popover
                open={open}
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery("");
                }}
                side="bottom"
                align="start"
                label={t`Recent conversations`}
                trigger={
                    <button
                        type="button"
                        className="bilda-chat-panel__picker"
                        aria-label={t`Switch conversation: ${title}`}
                    >
                        <span className="bilda-chat-panel__picker-title">
                            {title}
                        </span>
                        <Icon name="chevron-down" />
                    </button>
                }
            >
                <div className="bilda-chat-panel__picker-panel">
                    {status === "ready" && rows.length > SEARCH_FROM ? (
                        <Input
                            type="search"
                            size="sm"
                            aria-label={t`Find a conversation`}
                            placeholder={t`Find a conversation`}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    ) : (
                        <h3 className="bilda-chat-panel__picker-heading">
                            {t`Recent`}
                        </h3>
                    )}
                    {list}
                </div>
            </Popover>
        );
    return (
        <div className="bilda-chat-panel__head-row">
            {head}
            <Button
                variant="secondary"
                size="md"
                className="bilda-chat-panel__new"
                aria-label={t`New conversation`}
                onClick={onNew}
            >
                {t`New`}
                <Icon name="plus" size="sm" />
            </Button>
            {actions !== undefined ? (
                <Menu
                    align="end"
                    // Both picks move the focus on: Rename into its field,
                    // Delete into the host's dialog.
                    returnFocus={() => false}
                    trigger={
                        <IconButton
                            label={t`Conversation actions`}
                            variant="ghost"
                            size="sm"
                        >
                            <Icon name="overflow" />
                        </IconButton>
                    }
                    items={[
                        {
                            _tag: "item",
                            label: t`Rename`,
                            icon: <Icon name="pencil" />,
                            onSelect: () => setRenaming(actions.current),
                        },
                        {
                            _tag: "item",
                            label: t`Delete…`,
                            icon: <Icon name="trash" />,
                            tone: "danger",
                            onSelect: actions.onDelete,
                        },
                    ]}
                />
            ) : null}
        </div>
    );
};
